import PropTypes from 'prop-types';
import { BsCheckCircleFill, BsArrowRight } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

const KANAL_LABELS = {
	post_mailing: 'Post-Mailing',
	telefon: 'Telefon',
	email: 'E-Mail',
	linkedin: 'LinkedIn',
};

const EMPFEHLUNGSSTAERKE_COLORS = {
	sehr_hoch: 'bg-success-200 text-success-700',
	hoch: 'bg-primary-200 text-primary-700',
	mittel: 'bg-caution-200 text-caution-700',
	niedrig: 'bg-bgBase-400 text-bgBase-800',
};

/**
 * Shows the generated strategy summary after pipeline creation.
 * Renders data from generation.crmData.
 */
const StrategySuccessView = ({ generation, onNavigate }) => {
	const crmData = generation?.crmData;
	const kunde = crmData?.kunde;
	const kanaele = crmData?.kanal_priorisierung ?? [];
	const sequenz = crmData?.empfohlene_sequenz;

	// Fallback: if crmData is empty (default pipeline was used), show minimal view
	const hasCrmData = crmData && kanaele.length > 0;

	return (
		<div className="space-y-4 sm:space-y-6">
			<div className="flex items-start gap-3">
				<div className="h-10 w-10 rounded-full bg-success-200 text-success-700 flex items-center justify-center flex-shrink-0">
					<BsCheckCircleFill className="w-5 h-5" />
				</div>
				<div>
					<h2 className="text-lg font-semibold text-text">Ihre Vertriebsstrategie ist fertig!</h2>
					<p className="text-sm text-bgBase-800">
						Pipeline und Outreach-Strategie wurden erstellt{kunde?.name ? ` für ${kunde.name}` : ''}.
					</p>
				</div>
			</div>

			{hasCrmData ? (
				<>
					{crmData.analyse_zusammenfassung && (
						<div className="bg-bgBase-300 rounded-card p-3 sm:p-4">
							<h3 className="text-sm font-semibold text-text mb-1">Analyse</h3>
							<p className="text-sm text-text">{crmData.analyse_zusammenfassung}</p>
						</div>
					)}

					{/* Kanal-Priorisierung */}
					<div>
						<h3 className="text-sm font-semibold text-text mb-3">Priorisierte Outreach-Strategie</h3>
						<div className="space-y-2">
							{kanaele.map((kanal) => (
								<div
									key={kanal.rang}
									className="flex items-start gap-3 p-3 bg-white border border-bgBase-400 rounded-card"
								>
									<div className="h-7 w-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
										{kanal.rang}
									</div>
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 mb-0.5">
											<span className="text-sm font-medium text-text">
												{KANAL_LABELS[kanal.kanal] ?? kanal.kanal}
											</span>
											<span
												className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${EMPFEHLUNGSSTAERKE_COLORS[kanal.empfehlungsstaerke] ?? EMPFEHLUNGSSTAERKE_COLORS.niedrig}`}
											>
												{kanal.empfehlungsstaerke?.replace('_', ' ')}
											</span>
										</div>
										<p className="text-xs text-bgBase-800">{kanal.begruendung}</p>
										{kanal.erwartete_response_rate_prozent && (
											<div className="mt-1 text-[11px] text-bgBase-800">
												<span>Response: {kanal.erwartete_response_rate_prozent}</span>
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Playbook / Sequenz */}
					{sequenz?.ablauf?.length > 0 && (
						<div>
							<h3 className="text-sm font-semibold text-text mb-2">
								Playbook: {sequenz.name ?? 'Empfohlene Sequenz'}
							</h3>
							<div className="space-y-1.5">
								{sequenz.ablauf.map((step, idx) => (
									<div key={idx} className="flex items-start gap-1.5 sm:gap-2 text-sm">
										<span className="text-xs font-mono text-bgBase-800 w-12 flex-shrink-0 pt-0.5">
											Tag {step.tag}
										</span>
										<span className="text-xs font-medium text-primary-700 w-16 sm:w-20 flex-shrink-0 pt-0.5">
											{KANAL_LABELS[step.kanal] ?? step.kanal}
										</span>
										<span className="text-xs text-text">{step.aktion}</span>
									</div>
								))}
							</div>
						</div>
					)}
				</>
			) : (
				/* Fallback when default pipeline was used */
				<div className="bg-bgBase-300 rounded-card p-4">
					<p className="text-sm text-text">
						Ihre Pipeline mit Standard-Stages wurde erstellt. Sie können die Stages jederzeit im
						Pipeline-Board anpassen.
					</p>
				</div>
			)}

			{/* CTA */}
			<div className="flex justify-end pt-2">
				<Button variant="solid" onClick={onNavigate}>
					Zur Pipeline
					<BsArrowRight className="ml-2 h-4 w-4" />
				</Button>
			</div>
		</div>
	);
};

StrategySuccessView.propTypes = {
	generation: PropTypes.object.isRequired,
	onNavigate: PropTypes.func.isRequired,
};

export default StrategySuccessView;
